/**
 * AISetupModal — Project-only natural-language creation.
 *
 * Account creation has been removed from this flow — it is handled outside
 * via AWS / account setup.  This modal only creates new projects under an
 * existing account selected by the user on the confirm page.
 *
 * Steps: INPUT → PARSING → CONFIRM → EXECUTING → DONE | ERROR
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import {
  X, Sparkles, Send, Check, AlertCircle,
  Loader2, ChevronRight, Building2, FolderPlus, RotateCcw,
} from 'lucide-react'
import { useAccounts, useApplications } from '../../hooks'
import { useApp } from '../../context'
import './AISetupModal.css'

const API = import.meta.env.PROD
  ? 'https://recodeit.ai/api'
  : 'http://localhost:8000'

function AISetupModal({ isOpen, onClose, onSuccess, selectedAccountId }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [step, setStep]             = useState('input')
  const [inputText, setInputText]   = useState('')
  const [error, setError]           = useState(null)

  // Extracted / editable fields — project-only
  const [projectName, setProjectName]   = useState('')
  const [createProject, setCreateProj]  = useState(false)
  const [summary, setSummary]           = useState('')

  // Account selection on confirm page — dropdown of existing accounts
  const [pickedAccountId, setPickedAccountId] = useState('')

  // Execution
  const [execStatus, setExecStatus] = useState([])

  const inputRef = useRef(null)
  const { accounts, loadAccounts } = useAccounts()
  const { createApplication, loadApplications } = useApplications()
  const { db } = useApp()

  // Resolve the currently-selected account (from parent context)
  const currentAccount = useMemo(() => {
    if (!selectedAccountId) return null
    return accounts.find(a => (a.scoutAccountId || a.id) === selectedAccountId) || null
  }, [accounts, selectedAccountId])

  // Focus on open
  useEffect(() => {
    if (isOpen && step === 'input') {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, step])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep('input'); setInputText(''); setError(null)
      setProjectName(''); setCreateProj(false)
      setSummary(''); setExecStatus([])
      setPickedAccountId('')
    }
  }, [isOpen])

  if (!isOpen) return null

  // ── Parse ──────────────────────────────────────────────────────────────────
  const handleParse = async () => {
    if (!inputText.trim()) return
    setStep('parsing')
    setError(null)

    try {
      const token = localStorage.getItem('modernizeit_token')
      const res = await fetch(`${API}/ai/setup/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: inputText,
          existing_accounts: accounts.map(a => ({ id: a.scoutAccountId || a.id, name: a.name })),
          current_account_name: currentAccount?.name || null,
          current_account_id: currentAccount?.scoutAccountId || currentAccount?.id || null,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Parse failed (${res.status})`)
      }

      const data = await res.json()
      console.log('[AISetup] Extraction result:', data)

      if (data.error) {
        setError(data.error)
        setStep('input')
        return
      }

      // ── Project-only guard ──────────────────────────────────────────────
      // Account creation is no longer supported in this flow.
      // We only proceed if a project name was recognised.
      if (!data.create_project || !data.project_name) {
        setError("I can only create new projects here. Try: 'Create project ClaimsModel'")
        setStep('input')
        return
      }

      // Default account: prefer the parent-selected account, then first available
      const defaultAcctId =
        selectedAccountId ||
        (accounts[0] ? (accounts[0].scoutAccountId || accounts[0].id) : '') ||
        ''

      setCreateProj(true)
      setProjectName(data.project_name || '')
      setPickedAccountId(defaultAcctId)
      setSummary(`Create project "${data.project_name}"`)
      setStep('confirm')
    } catch (err) {
      console.error('[AISetup] Parse error:', err)
      setError(err.message)
      setStep('input')
    }
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  const canConfirm = createProject && projectName.trim().length > 0 && pickedAccountId !== ''

  // ── Execute ────────────────────────────────────────────────────────────────
  const handleExecute = async () => {
    if (!canConfirm) return
    setStep('executing')
    setError(null)
    const steps = []

    try {
      // Resolve account name from the selected account id
      const selectedAcct = accounts.find(a => (a.scoutAccountId || a.id) === pickedAccountId)
      const acctName = selectedAcct?.name || ''

      steps.push({ label: `Creating project "${projectName}"...`, status: 'running' })
      setExecStatus([...steps])

      const app = await createApplication({
        name: projectName,
        description: '',
        scoutAccountId: pickedAccountId,
        accountName: acctName,
        sourceLanguage: 'cobol',
        targetLanguage: 'java',
      })

      steps[steps.length - 1] = { label: `Project "${projectName}" created`, status: 'done', result: app }
      setExecStatus([...steps])

      await loadAccounts()
      await loadApplications()
      setStep('done')
    } catch (err) {
      console.error('[AISetup] Execution error:', err)
      steps.push({ label: err.message, status: 'error' })
      setExecStatus([...steps])
      setError(err.message)
      setStep('error')
    }
  }

  // ── Done → route to guided upload ──────────────────────────────────────────
  const handleDone = () => {
    const created = execStatus.find(s => s.status === 'done' && s.result)
    onClose()
    if (created && onSuccess) onSuccess(created.result)
  }

  // ── Key handler ────────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleParse() }
  }

  // ── Project-only example chips ─────────────────────────────────────────────
  const examples = [
    'Create project ClaimsModel',
    'Create a project named LegacyMigration',
    'New project PayrollModernization',
  ]

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ai-setup-overlay" onClick={onClose}>
      <div className="ai-setup-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="ai-setup-header">
          <div className="ai-setup-header-left">
            <Sparkles size={20} className="ai-setup-icon" />
            <h2>Get Started with AI</h2>
          </div>
          <button className="ai-setup-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ai-setup-body">

          {/* ── INPUT ─────────────────────────────────────────────── */}
          {step === 'input' && (
            <div className="ai-setup-input-step">
              <p className="ai-setup-description">
                Tell me the project name in plain English, and I'll set it up under an existing account.
              </p>

              {currentAccount && (
                <div className="ai-setup-context-badge">
                  <Building2 size={14} />
                  <span>Current account: <strong>{currentAccount.name}</strong></span>
                </div>
              )}

              {error && (
                <div className="ai-setup-error">
                  <AlertCircle size={16} /><span>{error}</span>
                </div>
              )}

              <div className="ai-setup-input-area">
                <textarea
                  ref={inputRef}
                  className="ai-setup-textarea"
                  placeholder='e.g., "Create project ClaimsModel" or "New project LegacyMigration"'
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={3}
                />
                <button className="ai-setup-send" onClick={handleParse} disabled={!inputText.trim()}>
                  <Send size={18} />
                </button>
              </div>

              <div className="ai-setup-examples">
                <span className="ai-setup-examples-label">Try:</span>
                {examples.map((ex, i) => (
                  <button key={i} className="ai-setup-example" onClick={() => setInputText(ex)}>{ex}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── PARSING ───────────────────────────────────────────── */}
          {step === 'parsing' && (
            <div className="ai-setup-parsing">
              <Loader2 size={32} className="spin" />
              <p>Understanding your request...</p>
            </div>
          )}

          {/* ── CONFIRM ───────────────────────────────────────────── */}
          {step === 'confirm' && (
            <div className="ai-setup-confirm">
              <p className="ai-setup-review-summary">{summary}</p>

              <div className="ai-setup-actions-list">
                {createProject && (
                  <div className="ai-setup-action-card">
                    <div className="ai-setup-action-header">
                      <FolderPlus size={18} style={{ color: '#388bfd' }} />
                      <span className="ai-setup-action-label">Create Project</span>
                    </div>
                    <div className="ai-setup-action-fields">

                      {/* Project name — editable */}
                      <div className="ai-setup-field">
                        <span className="ai-setup-field-label">Project Name:</span>
                        <input
                          className="ai-setup-field-input"
                          value={projectName}
                          onChange={e => setProjectName(e.target.value)}
                          placeholder="Project name"
                        />
                      </div>

                      {/* Account — dropdown of existing accounts only */}
                      <div className="ai-setup-field">
                        <span className="ai-setup-field-label">In Account:</span>
                        {accounts.length === 0 ? (
                          <div className="ai-setup-no-accounts">
                            <AlertCircle size={13} />
                            <span>No accounts available. Create or select an account before creating a project.</span>
                          </div>
                        ) : (
                          <select
                            className="ai-setup-field-select"
                            value={pickedAccountId}
                            onChange={e => setPickedAccountId(e.target.value)}
                          >
                            <option value="">— Select account —</option>
                            {accounts.map(a => {
                              const id = a.scoutAccountId || a.id
                              return <option key={id} value={id}>{a.name}</option>
                            })}
                          </select>
                        )}
                      </div>

                    </div>
                  </div>
                )}
              </div>

              <div className="ai-setup-review-actions">
                <button className="ai-setup-btn secondary" onClick={() => { setStep('input'); setError(null) }}>
                  <RotateCcw size={16} /><span>Start Over</span>
                </button>
                <button
                  className={`ai-setup-btn primary${canConfirm ? '' : ' disabled'}`}
                  onClick={handleExecute}
                  disabled={!canConfirm}
                >
                  <Check size={16} /><span>Confirm & Create</span>
                </button>
              </div>
            </div>
          )}

          {/* ── EXECUTING / DONE / ERROR ──────────────────────────── */}
          {(step === 'executing' || step === 'done' || step === 'error') && (
            <div className="ai-setup-execution">
              {execStatus.map((s, i) => (
                <div key={i} className={`ai-setup-exec-item ${s.status}`}>
                  {s.status === 'running'  && <Loader2    size={16} className="spin"       />}
                  {s.status === 'done'     && <Check      size={16} className="text-green"  />}
                  {s.status === 'skipped'  && <Check      size={16} className="text-blue"   />}
                  {s.status === 'error'    && <AlertCircle size={16} className="text-red"  />}
                  <span className="ai-setup-exec-name">{s.label}</span>
                </div>
              ))}

              {step === 'done' && (
                <div className="ai-setup-done">
                  <p>Setup complete! Let's upload your source code.</p>
                  <button className="ai-setup-btn primary" onClick={handleDone}>
                    <ChevronRight size={16} /><span>Upload Source Code</span>
                  </button>
                </div>
              )}

              {step === 'error' && (
                <div className="ai-setup-done">
                  <div className="ai-setup-error">
                    <AlertCircle size={16} /><span>{error || 'Something went wrong.'}</span>
                  </div>
                  <div className="ai-setup-review-actions">
                    <button className="ai-setup-btn secondary" onClick={onClose}>Close</button>
                    <button className="ai-setup-btn primary" onClick={handleDone}>Continue Anyway</button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AISetupModal
